import { Components } from '@mui/material';
import { Theme } from '@mui/material/styles';

type RadioVisualState = {
  borderColor: string;
  backgroundColor: string;
  dotColor?: string;
  opacity?: number;
  outline?: string;
  outlineOffset?: string;
};

type RadioStateStyles = {
  default: RadioVisualState;
  hover: RadioVisualState;
  focus: RadioVisualState;
  checked: RadioVisualState;
  checkedHover: RadioVisualState;
  disabled: RadioVisualState;
  disabledChecked: RadioVisualState;
};

const getRadioStates = (theme: Theme): RadioStateStyles => ({
  default: {
    borderColor: theme.palette.border.primaryDefault,
    backgroundColor: theme.palette.background.light,
  },
  hover: {
    borderColor: theme.palette.border.primaryHover,
    backgroundColor: theme.palette.background.light,
  },
  focus: {
    borderColor: theme.palette.border.primaryDefault,
    backgroundColor: theme.palette.background.light,
    outline: `2px solid ${theme.palette.border.focus}`,
    outlineOffset: '0',
  },
  checked: {
    borderColor: theme.palette.border.primaryDefault,
    backgroundColor: theme.palette.background.light,
    dotColor: theme.palette.background.brandPrimary,
  },
  checkedHover: {
    borderColor: theme.palette.border.primaryHover,
    backgroundColor: theme.palette.background.light,
    dotColor: theme.palette.background.brandPrimary,
  },
  disabled: {
    borderColor: theme.palette.border.primaryDefault,
    backgroundColor: theme.palette.background.light,
    opacity: theme.palette.action.disabledOpacity,
  },
  disabledChecked: {
    borderColor: theme.palette.border.primaryDefault,
    backgroundColor: theme.palette.background.light,
    dotColor: theme.palette.background.brandPrimary,
    opacity: theme.palette.action.disabledOpacity,
  },
});

const getRadioStateStyles = (theme: Theme, states: RadioStateStyles) => ({
  border: `1px solid ${states.default.borderColor}`,
  backgroundColor: states.default.backgroundColor,

  '& .MuiSvgIcon-root': {
    display: 'none',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '12px',
    height: '12px',
    flexShrink: 0,
    borderRadius: theme.lelyBorderRadius.MD,
    backgroundColor: 'transparent',
    transform: 'translate(-50%, -50%)',
  },

  '&[data-preview-state="default"]': {
    borderColor: states.default.borderColor,
    backgroundColor: states.default.backgroundColor,
  },

  '&:hover, &[data-preview-state="hover"]': {
    borderColor: states.hover.borderColor,
    backgroundColor: states.hover.backgroundColor,
  },

  '&.Mui-focusVisible, &[data-preview-state="focus"]': {
    borderColor: states.focus.borderColor,
    backgroundColor: states.focus.backgroundColor,
    outline: states.focus.outline,
    outlineOffset: states.focus.outlineOffset,
  },

  '&.Mui-checked, &[data-preview-state="checked"]': {
    borderColor: states.checked.borderColor,
    backgroundColor: states.checked.backgroundColor,

    '&::after': {
      backgroundColor: states.checked.dotColor,
    },
  },

  '&.Mui-checked:hover, &[data-preview-state="checked-hover"]': {
    borderColor: states.checkedHover.borderColor,
    backgroundColor: states.checkedHover.backgroundColor,

    '&::after': {
      backgroundColor: states.checkedHover.dotColor,
    },
  },

  '&.Mui-disabled': {
    borderColor: states.disabled.borderColor,
    backgroundColor: states.disabled.backgroundColor,
    opacity: states.disabled.opacity,
  },

  '&.Mui-disabled.Mui-checked, &[data-preview-state="disabled-checked"]': {
    borderColor: states.disabledChecked.borderColor,
    backgroundColor: states.disabledChecked.backgroundColor,
    opacity: states.disabledChecked.opacity,

    '&::after': {
      backgroundColor: states.disabledChecked.dotColor,
    },
  },
});

export const getRadioComponents = (
  theme: Theme,
): Pick<Components, 'MuiRadio'> => {
  const radioStates = getRadioStates(theme);

  return {
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
      styleOverrides: {
        root: {
          boxSizing: 'border-box',
          flexShrink: 0,
          position: 'relative',
          width: '24px',
          height: '24px',
          padding: 0,
          borderRadius: theme.lelyBorderRadius.XL,
          fontSize: theme.lelyFontSize.SM,
          lineHeight: theme.lelyLineHeight.BASE,

          '& .MuiTouchRipple-root': {
            display: 'none',
          },

          ...getRadioStateStyles(theme, radioStates),
        },
      },
    },
  };
};